import React from 'react';
import { motion } from 'framer-motion';

const TermsAndConditions = () => {
  const toc = [
    { id: "services", title: "1. OUR SERVICES" },
    { id: "ip", title: "2. INTELLECTUAL PROPERTY RIGHTS" },
    { id: "userreps", title: "3. USER REPRESENTATIONS" },
    { id: "purchases", title: "4. PURCHASES AND PAYMENT" },
    { id: "subscriptions", title: "5. SUBSCRIPTIONS" },
    { id: "prohibited", title: "6. PROHIBITED ACTIVITIES" },
    { id: "ugc", title: "7. USER GENERATED CONTRIBUTIONS" },
    { id: "license", title: "8. CONTRIBUTION LICENSE" },
    { id: "sitemanage", title: "9. SERVICES MANAGEMENT" },
    { id: "privacy", title: "10. PRIVACY POLICY" },
    { id: "termination", title: "11. TERM AND TERMINATION" },
    { id: "modifications", title: "12. MODIFICATIONS AND INTERRUPTIONS" },
    { id: "law", title: "13. GOVERNING LAW" },
    { id: "disputes", title: "14. DISPUTE RESOLUTION" },
    { id: "corrections", title: "15. CORRECTIONS" },
    { id: "disclaimer", title: "16. DISCLAIMER" },
    { id: "liability", title: "17. LIMITATIONS OF LIABILITY" },
    { id: "indemnification", title: "18. INDEMNIFICATION" },
    { id: "userdata", title: "19. USER DATA" },
    { id: "electronic", title: "20. ELECTRONIC COMMUNICATIONS, TRANSACTIONS, AND SIGNATURES" },
    { id: "sms", title: "21. SMS TEXT MESSAGING" },
    { id: "california", title: "22. CALIFORNIA USERS AND RESIDENTS" },
    { id: "misc", title: "23. MISCELLANEOUS" },
    { id: "contact", title: "24. CONTACT US" }
  ];

  const scrollToId = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // Account for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  return (
    <div className="min-h-screen bg-clean-white pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl font-bold text-slate-gray mb-4 uppercase">Terms and Conditions</h1>
          <p className="text-lg text-slate-gray opacity-70 mb-12">Last Updated: June 30, 2026</p>

          <div className="prose prose-lg max-w-none text-slate-gray leading-relaxed">
            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">AGREEMENT TO OUR LEGAL TERMS</h2>
              <p className="mb-6">
                We are <strong>EngageDent Ai, LLC</strong>, doing business as <strong>EngageDent Ai</strong> ("Company," "we," "us," "our"), a company registered in Massachusetts, United States at 56 Broad St STE 14296, Boston, MA 02109.
              </p>
              <p className="mb-6">
                We operate the website <a href="https://engagedent.com" className="text-dental-blue hover:underline">https://engagedent.com</a> (the "Site"), as well as any other related products and services that refer or link to these legal terms (the "Legal Terms") (collectively, the "Services").
              </p>
              <p className="mb-6">
                You can contact us by phone at <strong>6175468041</strong>, email at <a href="mailto:support@engagedent.com" className="text-dental-blue hover:underline">support@engagedent.com</a>, or by mail to <strong>56 Broad St STE 14296, Boston, MA 02109, United States</strong>.
              </p>
              <p className="mb-6">
                These Legal Terms constitute a legally binding agreement made between you, whether personally or on behalf of an entity ("you"), and EngageDent Ai, LLC, concerning your access to and use of the Services. You agree that by accessing the Services, you have read, understood, and agreed to be bound by all of these Legal Terms. <strong>IF YOU DO NOT AGREE WITH ALL OF THESE LEGAL TERMS, THEN YOU ARE EXPRESSLY PROHIBITED FROM USING THE SERVICES AND YOU MUST DISCONTINUE USE IMMEDIATELY.</strong>
              </p>
              <p className="mb-6">
                We will provide you with prior notice of any scheduled changes to the Services you are using. The modified Legal Terms will become effective upon posting or notifying you by support@engagedent.com, as stated in the email message. By continuing to use the Services after the effective date of any changes, you agree to be bound by the modified terms.
              </p>
              <p className="mb-6">
                The Services are intended for users who are at least 18 years old. Persons under the age of 18 are not permitted to use or register for the Services.
              </p>
              <p>We recommend that you print a copy of these Legal Terms for your records.</p>
            </section>

            <nav className="bg-gray-50 rounded-2xl p-8 mb-16 border border-gray-100">
              <h2 className="text-2xl font-bold mb-6">TABLE OF CONTENTS</h2>
              <ul className="grid md:grid-cols-1 gap-2 list-none p-0 m-0">
                {toc.map((item) => (
                  <li key={item.id} className="m-0">
                    <button 
                      onClick={() => scrollToId(item.id)}
                      className="text-dental-blue hover:text-ai-teal transition-colors text-left font-medium"
                    >
                      {item.title}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            <section id="services" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">1. OUR SERVICES</h2>
              <p className="mb-4">
                The information provided when using the Services is not intended for distribution to or use by any person or entity in any jurisdiction or country where such distribution or use would be contrary to law or regulation or which would subject us to any registration requirement within such jurisdiction or country. Accordingly, those persons who choose to access the Services from other locations do so on their own initiative and are solely responsible for compliance with local laws, if and to the extent local laws are applicable.
              </p>
              <p className="mb-4">
                EngageDent Ai provides patient communication services to dental practices and may receive, store, or process protected health information ("PHI") on a practice's behalf. Where we handle PHI for a dental practice, we act as a Business Associate under the Health Insurance Portability and Accountability Act ("HIPAA"), and we maintain administrative, physical, and technical safeguards designed to protect that information in accordance with HIPAA and the Business Associate Agreement ("BAA") we enter into with the practice. 
              </p>
              <p className="mb-4">
                If you are a patient, the dental practice is the covered entity responsible for your PHI, and any rights you have regarding that information are handled through your dental practice. If you are a dental practice or other covered entity using the Services in connection with PHI, your use of the Services with respect to that PHI is governed by the BAA between you and us.
              </p>
            </section>

            <section id="ip" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">2. INTELLECTUAL PROPERTY RIGHTS</h2>
              <h3 className="text-xl font-bold mb-3">Our intellectual property</h3>
              <p className="mb-4">
                We are the owner or the licensee of all intellectual property rights in our Services, including all source code, databases, functionality, software, website designs, audio, video, text, photographs, and graphics in the Services (collectively, the "Content"), as well as the trademarks, service marks, and logos contained therein (the "Marks").
              </p>
              <p className="mb-4 italic">The Content and Marks are provided in or through the Services "AS IS" for your internal business purpose only.</p>
            </section>

            <section id="userreps" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">3. USER REPRESENTATIONS</h2>
              <p className="mb-4">By using the Services, you represent and warrant that: (1) you have the legal capacity and you agree to comply with these Legal Terms; (2) you are not a minor; (3) you will not access the Services through automated or non-human means; (4) you will not use the Services for any illegal or unauthorized purpose; and (5) your use of the Services will not violate any applicable law or regulation.</p>
            </section>

            <section id="purchases" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">4. PURCHASES AND PAYMENT</h2>
              <p className="mb-4">We accept the following forms of payment: Visa, Mastercard, American Express, and Discover.</p>
              <p className="mb-4">
                You agree to provide current, complete, and accurate purchase and account information for all purchases made via the Services. You further agree to promptly update account and payment information, including email address, payment method, and payment card expiration date.
              </p>
            </section>

            <section id="subscriptions" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">5. SUBSCRIPTIONS</h2>
              <p className="mb-4">Your subscription will continue and automatically renew unless canceled. You consent to our charging your payment method on a recurring basis without requiring your prior approval for each recurring charge.</p>
            </section>

            <section id="prohibited" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">6. PROHIBITED ACTIVITIES</h2>
              <p className="mb-4">You may not access or use the Services for any purpose other than that for which we make the Services available. Prohibited activities include data mining, harassing staff, attempting to bypass security, or using the Services to compete with us.</p>
            </section>

            <section id="ugc" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">7. USER GENERATED CONTRIBUTIONS</h2>
              <p className="mb-4">The Services does not offer users to submit or post content. We may provide you with the opportunity to create or submit materials (collectively, "Contributions"). Contributions may be viewable by other users.</p>
            </section>

            <section id="license" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">8. CONTRIBUTION LICENSE</h2>
              <p className="mb-4">You and Services agree that we may access, store, process, and use any information and personal data that you provide following the terms of the Privacy Policy.</p>
            </section>

            <section id="sitemanage" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">9. SERVICES MANAGEMENT</h2>
              <p className="mb-4">We reserve the right to monitor the Services for violations of these Legal Terms and take appropriate legal action against anyone who violates the law.</p>
            </section>

            <section id="privacy" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">10. PRIVACY POLICY</h2>
              <p className="mb-4">We care about data privacy and security. Please review our Privacy Policy: <a href="https://engagedent.com/privacy-policy" className="text-dental-blue hover:underline">https://engagedent.com/privacy-policy</a>. By using the Services, you agree to be bound by our Privacy Policy.</p>
            </section>

            <section id="termination" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">11. TERM AND TERMINATION</h2>
              <p className="mb-4">These Legal Terms shall remain in full force and effect while you use the Services. We reserve the right to deny access to and use of the Services to any person for any reason.</p>
            </section>

            <section id="modifications" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">12. MODIFICATIONS AND INTERRUPTIONS</h2>
              <p className="mb-4">We reserve the right to change, modify, or remove the contents of the Services at any time or for any reason at our sole discretion without notice.</p>
            </section>

            <section id="law" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">13. GOVERNING LAW</h2>
              <p className="mb-4">These Legal Terms and your use of the Services are governed by and construed in accordance with the laws of the Commonwealth of Massachusetts.</p>
            </section>

            <section id="disputes" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">14. DISPUTE RESOLUTION</h2>
              <p className="mb-4">Any dispute will be finally and exclusively resolved by binding arbitration. YOU UNDERSTAND THAT WITHOUT THIS PROVISION, YOU WOULD HAVE THE RIGHT TO SUE IN COURT AND HAVE A JURY TRIAL.</p>
            </section>

            <section id="corrections" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">15. CORRECTIONS</h2>
              <p className="mb-4">There may be information on the Services that contains typographical errors, inaccuracies, or omissions. We reserve the right to correct any errors and to change or update information without prior notice.</p>
            </section>

            <section id="disclaimer" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">16. DISCLAIMER</h2>
              <p className="mb-4 uppercase">The services are provided on an as-is and as-available basis. You agree that your use of the services will be at your sole risk.</p>
            </section>

            <section id="liability" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">17. LIMITATIONS OF LIABILITY</h2>
              <p className="mb-4 uppercase">In no event will we or our directors, employees, or agents be liable to you or any third party for any direct, indirect, consequential, exemplary, incidental, special, or punitive damages.</p>
            </section>

            <section id="indemnification" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">18. INDEMNIFICATION</h2>
              <p className="mb-4">You agree to defend, indemnify, and hold us harmless from and against any loss, damage, liability, claim, or demand due to or arising out of your use of the Services.</p>
            </section>

            <section id="userdata" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">19. USER DATA</h2>
              <p className="mb-4">We will maintain certain data that you transmit to the Services for the purpose of managing the performance of the Services. You are solely responsible for all data that you transmit.</p>
            </section>

            <section id="electronic" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">20. ELECTRONIC COMMUNICATIONS, TRANSACTIONS, AND SIGNATURES</h2>
              <p className="mb-4">Visiting the Services, sending us emails, and completing online forms constitute electronic communications. You consent to receive electronic communications.</p>
            </section>

            <section id="sms" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">21. SMS TEXT MESSAGING</h2>
              <h3 className="text-xl font-bold mb-2">Program Description</h3>
              <p className="mb-4">
                By providing your mobile phone number and opting in, you expressly consent to receive recurring text messages (SMS) from EngageDent Ai, including messages we send on behalf of the dental practice you are working with. These messages may include appointment reminders, scheduling confirmations, insurance information, and other updates related to your care.
              </p>
              <h3 className="text-xl font-bold mb-2">Opting Out</h3>
              <p className="mb-4">
                You can opt out of receiving SMS messages at any time by replying <strong>STOP</strong> to any message. After you opt out, you will receive a one-time confirmation message and will no longer receive SMS messages from the program.
              </p>
              <p className="mb-4"><strong>Help:</strong> Reply HELP to any message or email <a href="mailto:support@engagedent.com" className="text-dental-blue hover:underline">support@engagedent.com</a>.</p>
              <p><strong>Privacy:</strong> We share this information only with the service providers that help us deliver these messages. We do not sell your SMS opt-in data.</p>
            </section>

            <section id="california" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">22. CALIFORNIA USERS AND RESIDENTS</h2>
              <p className="mb-4">If any complaint with us is not satisfactorily resolved, you can contact the Complaint Assistance Unit of the Division of Consumer Services of the California Department of Consumer Affairs.</p>
            </section>

            <section id="misc" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">23. MISCELLANEOUS</h2>
              <p className="mb-4">These Legal Terms constitute the entire agreement between you and us. Our failure to exercise or enforce any right or provision shall not operate as a waiver of such right or provision.</p>
            </section>

            <section id="contact" className="mb-12 scroll-mt-24 border-t border-gray-100 pt-12">
              <h2 className="text-2xl font-bold mb-6">24. CONTACT US</h2>
              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
                <p className="font-bold text-xl mb-2 text-slate-gray">EngageDent Ai, LLC</p>
                <p className="mb-1">56 Broad St STE 14296</p>
                <p className="mb-1">Boston, MA 02109</p>
                <p className="mb-4">United States</p>
                <div className="space-y-1">
                  <p><strong>Phone:</strong> 6175468041</p>
                  <p><strong>Email:</strong> <a href="mailto:support@engagedent.com" className="text-dental-blue hover:underline">support@engagedent.com</a></p>
                </div>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsAndConditions;